import type { Ring, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../../solids';
import { panelWall } from '../../panels';
import { edgePanel, roofUv, type MasonryStyles } from '../masonry';

/** An axis-aligned plan rectangle: u along the building's front, v into it. */
export interface Rect {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

/** A wall's outline in its own panel: along it from 0 to `length`, up from y0 to the eaves and a gable apex over its middle. */
export function gableOutline(length: number, y0: number, eaveY: number, apexY: number): Ring {
  return [
    [0, y0],
    [length, y0],
    [length, eaveY],
    [length / 2, apexY],
    [0, eaveY],
  ];
}

function slope(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, points: readonly Vec3[], outward: Vec3): void {
  orientedFace(builder, styles.roof, frame, points, outward, roofUv(points[0]!, points[1]!, points[points.length - 1]!));
}

/** The two slopes of a roof whose ridge runs back from the front (along v) over the middle of the rect. */
export function transverseRoof(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  r: Rect,
  heights: { readonly eaveY: number; readonly ridgeY: number }
): void {
  const uc = (r.u0 + r.u1) / 2;
  const { eaveY, ridgeY } = heights;
  slope(builder, styles, frame, [planPoint(r.u0, eaveY, r.v1), planPoint(r.u0, eaveY, r.v0), planPoint(uc, ridgeY, r.v0), planPoint(uc, ridgeY, r.v1)], [-1, 1, 0]);
  slope(builder, styles, frame, [planPoint(r.u1, eaveY, r.v0), planPoint(r.u1, eaveY, r.v1), planPoint(uc, ridgeY, r.v1), planPoint(uc, ridgeY, r.v0)], [1, 1, 0]);
}

/**
 * A roof along u at one pitch: gabled at u0, hipped at u1, the hip running back half the depth.
 * The gable triangle at u0 is its end wall above the eaves; returns the ridge height.
 */
export function halfHipRoof(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  r: Rect,
  spec: { readonly eaveY: number; readonly pitch: number }
): number {
  const half = (r.v1 - r.v0) / 2;
  const vc = r.v0 + half;
  const ridgeY = spec.eaveY + half * Math.tan(spec.pitch);
  const e = (u: number, v: number) => planPoint(u, spec.eaveY, v);
  const ridgeA = planPoint(r.u0, ridgeY, vc);
  const ridgeB = planPoint(r.u1 - half, ridgeY, vc);
  slope(builder, styles, frame, [e(r.u0, r.v0), e(r.u1, r.v0), ridgeB, ridgeA], [0, 1, 1]);
  slope(builder, styles, frame, [e(r.u1, r.v1), e(r.u0, r.v1), ridgeA, ridgeB], [0, 1, -1]);
  slope(builder, styles, frame, [e(r.u1, r.v0), e(r.u1, r.v1), ridgeB], [1, 1, 0]);
  orientedFace(builder, styles.wall, frame, [e(r.u0, r.v0), e(r.u0, r.v1), ridgeA], [-1, 0, 0]);
  return ridgeY;
}

/** A gabled bay rising from a hall's eaves on its front at v0, running back `depth` into the roof. */
export interface CrossGable {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly depth: number;
  readonly eaveY: number;
  readonly apexY: number;
}

/** A closed triangular prism: the gable wall with its openings, the two slopes and a back gable buried in the roof. */
export function crossGableSolid(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  g: CrossGable,
  openings: readonly Ring[]
): void {
  const width = g.u1 - g.u0;
  const outline: Ring = [
    [0, g.eaveY],
    [width, g.eaveY],
    [width / 2, g.apexY],
  ];
  const v1 = g.v0 + g.depth;
  panelWall(builder, styles, frame, edgePanel([g.u0, g.v0], [g.u1, g.v0]), outline, openings);
  panelWall(builder, { wall: styles.wall, opening: styles.wall }, frame, edgePanel([g.u1, v1], [g.u0, v1]), outline, []);
  transverseRoof(builder, styles, frame, { u0: g.u0, u1: g.u1, v0: g.v0, v1 }, { eaveY: g.eaveY, ridgeY: g.apexY });
}
